import { ThreeBounce } from 'better-react-spinkit';
import Button from 'components/button/Button';
import { Modal } from 'components/modal/Modal';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './../ConnectionsPage.scss';
import { endpoints } from 'endpoints.config';
import api, { ApiResponse } from 'api';
import { Toast, ToastMessageReason } from 'helpers/toast';
import { GeneralErrorMessages } from 'helpers/categoryHelpers/processErrors';
import { ItemConnectionModel } from '../items/ItemConnection';
type PropsModalConnectionDetail = {
    closeModal: (isRefresh: boolean) => void;
    selectedConnection: ItemConnectionModel;
    makePayment?: (connection: ItemConnectionModel) => void;
};

export const ModalConnectionDetail = ({
    closeModal,
    selectedConnection,
    makePayment,
}: PropsModalConnectionDetail) => {
    const { t } = useTranslation('network');
    const [isAddingConnection, setAddingConnection] = useState(false);
    const [isRemovingConnection, setRemovingConnection] = useState(false);
    const [type, setType] = useState<string | undefined>();

    const handleMakePayment = useCallback(() => {
        makePayment?.(selectedConnection);
        closeModal(false);
    }, [closeModal, makePayment, selectedConnection]);
    const handleAddConnection = useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            setAddingConnection(true);
            const actionType = e.currentTarget.id;
            setType(actionType);
            try {
                const payload = {
                    id: selectedConnection.senderId,
                    type: actionType,
                };
                const res = await api.post<ApiResponse>(
                    endpoints.networkmodule.acceptConnectionRequest,
                    payload
                );
                if (res.data?.status === '1') {
                    Toast.openToastMessage(
                        actionType === 'ACCEPTED' ? 'Request Accepted!' : 'Request Rejected!',
                        ToastMessageReason.VALID
                    );
                    selectedConnection.networkConnectionStatus = actionType;
                    closeModal(true);
                } else {
                    Toast.openToastMessage(GeneralErrorMessages.Generic, ToastMessageReason.ERROR);
                }

                setAddingConnection(false);
            } catch (ex: any) {
                if (ex?.response?.data?.errors?.length > 0) {
                    const key = ex.response.data.errors[0]
                        .messageCode as keyof typeof GeneralErrorMessages;
                    Toast.openToastMessage(
                        GeneralErrorMessages[key] ?? GeneralErrorMessages.Generic,
                        ToastMessageReason.ERROR
                    );
                }
                setAddingConnection(false);
            }
            setType(undefined);
        },
        [closeModal, selectedConnection]
    );
    const handleCancelRequest = useCallback(
        async (e: any) => {
            setAddingConnection(true);
            setType(e.currentTarget.id);
            try {
                const payload = {
                    id: selectedConnection.receiverId,
                    type: 'CANCEL',
                };
                const res = await api.post<ApiResponse>(
                    endpoints.networkmodule.connectionRequest,
                    payload
                );
                if (res.data?.status === '1') {
                    Toast.openToastMessage('Request Canceled!', ToastMessageReason.VALID);
                    closeModal(true);
                } else {
                    Toast.openToastMessage(GeneralErrorMessages.Generic, ToastMessageReason.ERROR);
                }

                setAddingConnection(false);
            } catch (ex: any) {
                if (ex?.response?.data?.errors?.length > 0) {
                    const key = ex.response.data.errors[0]
                        .messageCode as keyof typeof GeneralErrorMessages;
                    Toast.openToastMessage(
                        GeneralErrorMessages[key] ?? GeneralErrorMessages.Generic,
                        ToastMessageReason.ERROR
                    );
                }
                setAddingConnection(false);
            }
            setType(undefined);
        },
        [closeModal, selectedConnection.receiverId]
    );
    const handleRemoveRequest = useCallback(
        async (e: any) => {
            setRemovingConnection(true);
            setType(e.currentTarget.id);
            try {
                const payload = {
                    id: selectedConnection.connectionID,
                    type: 'REMOVE',
                };
                const res = await api.post<ApiResponse>(
                    endpoints.networkmodule.connectionRequest,
                    payload
                );
                if (res.data?.status === '1') {
                    Toast.openToastMessage('Request Canceled!', ToastMessageReason.VALID);
                    closeModal(true);
                } else {
                    Toast.openToastMessage(GeneralErrorMessages.Generic, ToastMessageReason.ERROR);
                }

                setRemovingConnection(false);
            } catch (ex: any) {
                if (ex?.response?.data?.errors?.length > 0) {
                    const key = ex.response.data.errors[0]
                        .messageCode as keyof typeof GeneralErrorMessages;
                    Toast.openToastMessage(
                        GeneralErrorMessages[key] ?? GeneralErrorMessages.Generic,
                        ToastMessageReason.ERROR
                    );
                }
                setRemovingConnection(false);
            }
            setType(undefined);
        },
        [closeModal, selectedConnection.connectionID]
    );
    const modalTitle = useMemo(() => {
        if (selectedConnection.networkConnectionStatus === 'ACCEPTED') return 'Connection Detail';
        else if (selectedConnection.networkConnectionStatus === 'PENDING') {
            if (selectedConnection.customerId !== selectedConnection.senderId) {
                return 'Accept Connection Request';
            }
            return 'Connection Request';
        }
        return '';
    }, [selectedConnection]);
    const modalMsg = useMemo(() => {
        if (selectedConnection.networkConnectionStatus === 'PENDING') {
            if (selectedConnection.customerId === selectedConnection.receiverId) {
                return `Are you sure you want to accept ${selectedConnection.senderName} connection request?`;
            }
            return 'Cancel Connection Request?';
        }
        return null;
    }, [selectedConnection]);
    return (
        <Modal
            className="accept-connection-modal"
            title={modalTitle}
            onCloseModal={() => {
                closeModal(false);
            }}
        >
            <div className="container">
                {selectedConnection.networkConnectionStatus === 'ACCEPTED' && (
                    <div className={'Layout'}>
                        <div>
                            <h4>Connection:</h4>
                            <p>
                                {selectedConnection.customerId == selectedConnection.senderId
                                    ? selectedConnection.receiverName
                                    : selectedConnection.senderName}
                            </p>
                        </div>
                        <div className="Buttons">
                            <Button
                                color="second"
                                id="REMOVE"
                                onClick={handleRemoveRequest}
                                disabled={isRemovingConnection}
                            >
                                {isRemovingConnection && type === 'REMOVE' ? (
                                    <ThreeBounce size={10} color={'#ffffff'} />
                                ) : (
                                    'Remove'
                                )}
                            </Button>
                            <Button color="first" onClick={handleMakePayment} id="MakePayment">
                                Make Payment
                            </Button>
                        </div>
                    </div>
                )}
                {modalMsg && <p>{modalMsg}</p>}
                {selectedConnection.customerId !== selectedConnection.senderId &&
                    selectedConnection.networkConnectionStatus === 'PENDING' && (
                        <div className="Buttons">
                            <Button
                                color="second"
                                onClick={handleAddConnection}
                                id="REJECTED"
                                disabled={isAddingConnection}
                            >
                                {isAddingConnection && type === 'REJECTED' ? (
                                    <ThreeBounce size={10} color={'#ffffff'} />
                                ) : (
                                    'Reject Request'
                                )}
                            </Button>
                            <Button
                                color="first"
                                onClick={handleAddConnection}
                                id="ACCEPTED"
                                disabled={isAddingConnection}
                            >
                                {isAddingConnection && type === 'ACCEPTED' ? (
                                    <ThreeBounce size={10} color={'#ffffff'} />
                                ) : (
                                    'Accept Request'
                                )}
                            </Button>
                        </div>
                    )}

                {selectedConnection.customerId === selectedConnection.senderId &&
                    selectedConnection.networkConnectionStatus === 'PENDING' && (
                        <div className="Buttons">
                            <Button
                                color="first"
                                onClick={handleCancelRequest}
                                id="CANCEL"
                                disabled={isAddingConnection}
                            >
                                {isAddingConnection && type === 'CANCEL' ? (
                                    <ThreeBounce size={10} color={'#ffffff'} />
                                ) : (
                                    t('cancelRequest')
                                )}
                            </Button>
                        </div>
                    )}
            </div>
        </Modal>
    );
};
